import { Component, computed, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Subscription, take } from 'rxjs';
import { selectDataByKey } from 'src/storage/general.selectors';
import { CardDataService } from 'src/app/sarlaft/services/card-data.service';

@Component({
  selector: 'app-pago-aprobado',
  templateUrl: './pago-aprobado.component.html',
  styleUrl: './pago-aprobado.component.scss',
})
export class PagoAprobadoComponent implements OnInit, OnDestroy {
  private readonly store = inject(Store);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly cardDataService = inject(CardDataService);

  private readonly subs = new Subscription();

  // Fuente principal de datos post-pago: mismo endpoint que sarlaft/P2P (getResumeDataPago=true).
  readonly cardData = signal<any>(null);

  readonly prepolicyid = signal('');
  readonly salesforceQuoteId = signal('');
  readonly codAsesor = signal('');
  readonly isLoading = signal(false);

  readonly property = toSignal(this.store.select(selectDataByKey('Property')), { initialValue: null });
  readonly tenant = toSignal(this.store.select(selectDataByKey('Tenant')), { initialValue: null });
  readonly policyHolder = toSignal(this.store.select(selectDataByKey('PolicyHolder')), { initialValue: null });
  // En arrendamiento el plan seleccionado se persiste en la key 'Plan' (savePlanToStore).
  readonly plan = toSignal(this.store.select(selectDataByKey('Plan')), { initialValue: null });
  readonly productCode = toSignal(this.store.select(selectDataByKey('ProductCode')), { initialValue: null });

  readonly planTitle = computed<string>(() => {
    const p: any = this.plan();
    const cd: any = this.cardData();
    const raw = p?.planName || cd?.planName || p?.name || p?.code || this.productCode() || '';
    return this.formatPlanName(raw);
  });

  readonly propertyAddress = computed<string>(() => {
    const p: any = this.property();
    const cd: any = this.cardData();
    return p?.PropertyAddress || p?.address || p?.direccion || cd?.PropertyAddress || 'No disponible';
  });

  readonly propertyCity = computed<string>(() => {
    const p: any = this.property();
    const cd: any = this.cardData();
    return p?.CityProperty || p?.city || p?.ciudad || cd?.PropertyCity || '';
  });

  readonly ownerName = computed<string>(() => {
    const cd: any = this.cardData();
    if (cd?.name) return cd.name;
    const ph: any = this.policyHolder();
    if (!ph) return 'No disponible';
    const fullname = [ph.FirstName, ph.SecondName, ph.LastName, ph.SecondLastName]
      .filter(Boolean).join(' ').trim();
    return fullname || 'No disponible';
  });

  readonly ownerDocumentType = computed<string>(() => {
    const ph: any = this.policyHolder();
    return String(ph?.DocumentType || 'CC');
  });

  readonly ownerDocumentNumber = computed<string>(() => {
    const ph: any = this.policyHolder();
    const raw = ph?.DocumentNumber;
    if (raw) return this.maskDocument(String(raw));
    const cd: any = this.cardData();
    return String(cd?.DocumentNumber || '');
  });

  readonly coverages = computed<string[]>(() => {
    const cd: any = this.cardData();
    if (Array.isArray(cd?.coverages) && cd.coverages.length) {
      return cd.coverages.map((n: string) => String(n)).filter(Boolean);
    }
    const p: any = this.plan();
    const list: any[] = p?.coverages ?? p?.coveragesGroup ?? [];
    return list
      .filter((c) => c?.include !== false && c?.isIncluded !== false)
      .map((c) => c?.name || '')
      .filter(Boolean);
  });

  readonly tenantName = computed<string>(() => {
    const t: any = this.tenant();
    if (!t) return '';
    const fullname = [t.FirstName, t.SecondName, t.LastName, t.SecondLastName]
      .filter(Boolean).join(' ').trim();
    return fullname;
  });

  readonly tipoPago = computed<'Mensual' | 'Anual'>(() => {
    const cd: any = this.cardData();
    const p: any = this.plan();
    const freq: string | undefined =
      cd?.payment_frequency || cd?.PaymentFrequency || p?.payment_frequency || p?.paymentFrequency;
    return freq === 'Monthly' || freq === 'Mensual' ? 'Mensual' : 'Anual';
  });

  readonly valorAPagar = computed<number>(() => {
    const cd: any = this.cardData();
    const p: any = this.plan();
    const anual = this.toNumber(
      cd?.totalAmount ?? cd?.priceAnual?.amount ??
      p?.priceAnual?.amount ?? p?.price?.full?.amount ?? p?.planPlus ?? p?.armaTuPlan ?? 0,
    );
    const mensual = this.toNumber(
      cd?.priceMensual?.amount ?? cd?.MonthlyAmount ??
      p?.priceMensual?.amount ?? p?.price?.price_monthly?.amount ?? p?.planEsencial ?? 0,
    );
    return this.tipoPago() === 'Mensual' ? mensual : anual;
  });

  readonly effectiveDate = computed<string>(() => {
    const cd: any = this.cardData();
    const p: any = this.property();
    return cd?.ContractStartDate || p?.ContractStartDate || '';
  });

  readonly paymentReference = computed<string>(() => {
    const cd: any = this.cardData();
    return cd?.idCotizacion || this.salesforceQuoteId() || 'N/A';
  });

  ngOnInit(): void {
    this.subs.add(
      this.route.queryParams.pipe(take(1)).subscribe((p) => {
        this.codAsesor.set(p['codigoAsesor'] || '');
        if (p['quoteId']) {
          this.salesforceQuoteId.set(String(p['quoteId']));
        } else {
          try {
            const stored = sessionStorage.getItem('sfQuoteId') || '';
            if (stored) this.salesforceQuoteId.set(stored);
          } catch {}
        }
        if (p['prepolicyid']) {
          // El prepolicyid viene URL-encoded en el queryParam; CardDataService espera el raw.
          const decoded = this.safeDecode(String(p['prepolicyid']));
          this.prepolicyid.set(decoded);
          this.loadCardData();
        }
      }),
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private loadCardData(): void {
    const id = this.prepolicyid();
    if (!id || this.cardData()) return;
    this.isLoading.set(true);
    this.subs.add(
      this.cardDataService.fetchInitialData(id, true).subscribe({
        next: (data) => {
          this.cardData.set(data);
          this.isLoading.set(false);
        },
        error: (err) => {
          console.error('[PagoAprobado] Error cargando cardData:', err);
          this.isLoading.set(false);
        },
      }),
    );
  }

  private safeDecode(value: string): string {
    try {
      return decodeURIComponent(value);
    } catch {
      return value;
    }
  }

  private toNumber(v: any): number {
    if (typeof v === 'number') return v;
    if (typeof v === 'string') {
      const n = Number(v.replace(/[^\d.-]/g, ''));
      return isNaN(n) ? 0 : n;
    }
    return 0;
  }

  // Enmascara la cedula: mantiene primeros 2 y ultimos 2 digitos, resto con "*".
  private maskDocument(value: string): string {
    const clean = String(value || '').trim();
    if (!clean) return '';
    if (clean.length <= 4) return clean;
    const start = clean.slice(0, 2);
    const end = clean.slice(-2);
    const middle = '*'.repeat(clean.length - 4);
    return `${start}${middle}${end}`;
  }

  // Antepone "Plan" si el planName no lo trae (ej. "Plus" -> "Plan Plus"), respeta "Arma tu plan".
  private formatPlanName(name: string): string {
    const n = String(name || '').trim();
    if (!n) return 'Seguro de arrendamiento';
    if (/^plan\b/i.test(n) || /^arma/i.test(n)) return n;
    return `Plan ${n}`;
  }

  entendido(): void {
    window.location.href = 'https://www.sura.co/';
  }
}
